/**
 * Password Reset Component
 * Handles password reset flow with token verification and new password setting
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Alert,
    CircularProgress,
    LinearProgress,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Tooltip
} from '@mui/material';
import {
    Lock as LockIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Security as SecurityIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../api/axiosInstance';
import { updateTokens } from '../features/auth/authSlice';

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: 500,
    margin: '0 auto',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    background: theme.palette.background.paper,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
}));

const PasswordReset = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth);

    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Password validation
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [passwordStrength, setPasswordStrength] = useState(0);

    // Auto-verify token if provided
    useEffect(() => {
        if (token) {
            handleTokenVerification(token);
        }
    }, [token]);

    const handleTokenVerification = async(verificationToken) => {
        setVerifying(true);
        setError(null);

        try {
            const response = await axiosInstance.get(`/api/v1/auth/verify-password-reset/${verificationToken}/`);

            if (response.data.success) {
                setVerificationStatus('verified');
            } else {
                setError(response.data.error);
                setVerificationStatus('failed');
            }
        } catch (error) {
            console.error('Token verification failed:', error);
            setError(error.response?.data?.error || 'Token verification failed');
            setVerificationStatus('failed');
        } finally {
            setVerifying(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate password if it's the password field
        if (field === 'newPassword') {
            validatePassword(value);
        }
    };

    const validatePassword = (password) => {
        const errors = [];
        let strength = 0;

        // Length check
        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
        } else {
            strength += 1;
        }

        // Uppercase check
        if (!/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter');
        } else {
            strength += 1;
        }

        // Lowercase check
        if (!/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter');
        } else {
            strength += 1;
        }

        // Number check
        if (!/\d/.test(password)) {
            errors.push('Password must contain at least one number');
        } else {
            strength += 1;
        }

        // Special character check
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.push('Password must contain at least one special character');
        } else {
            strength += 1;
        }

        setPasswordErrors(errors);
        setPasswordStrength(strength);
    };

    const handlePasswordReset = async() => {
        if (!formData.newPassword || !formData.confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (passwordErrors.length > 0) {
            setError('Please fix password validation errors');
            return;
        }

        setResetting(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/api/v1/auth/password-reset/', {
                token: token,
                new_password: formData.newPassword,
                new_password2: formData.confirmPassword
            });

            if (response.data.success) {
                setSuccess(true);
                toast.success('Password reset successfully!');

                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                setError(response.data.error);
            }
        } catch (error) {
            console.error('Password reset failed:', error);
            setError(error.response?.data?.error || 'Password reset failed');
        } finally {
            setResetting(false);
        }
    };

    const getStatusIcon = () => {
        switch (verificationStatus) {
            case 'verified':
                return <CheckIcon color = "success"
                sx = {
                    { fontSize: 48 } }
                />;
            case 'failed':
                return <ErrorIcon color = "error"
                sx = {
                    { fontSize: 48 } }
                />;
            default:
                return <LockIcon color = "primary"
                sx = {
                    { fontSize: 48 } }
                />;
        }
    };

    const getStatusColor = () => {
        switch (verificationStatus) {
            case 'verified':
                return 'success';
            case 'failed':
                return 'error';
            default:
                return 'primary';
        }
    };

    const getStatusMessage = () => {
        switch (verificationStatus) {
            case 'verified':
                return 'Token verified successfully!';
            case 'failed':
                return 'Token verification failed';
            default:
                return 'Verifying reset token...';
        }
    };

    const getStatusDescription = () => {
        switch (verificationStatus) {
            case 'verified':
                return 'Your reset token is valid. Please enter your new password below.';
            case 'failed':
                return 'The reset token is invalid or has expired. Please request a new password reset.';
            default:
                return 'Please wait while we verify your reset token.';
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 2) return 'error';
        if (passwordStrength < 4) return 'warning';
        return 'success';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength < 2) return 'Weak';
        if (passwordStrength < 4) return 'Medium';
        return 'Strong';
    };

    if (verifying) {
        return ( <
            Box display = "flex"
            flexDirection = "column"
            alignItems = "center"
            p = { 4 } >
            <
            CircularProgress size = { 48 }
            /> <
            Typography variant = "h6"
            sx = {
                { mt: 2 } } >
            Verifying reset token... <
            /Typography> <
            LinearProgress sx = {
                { width: '100%', mt: 2 } }
            /> <
            /Box>
        );
    }

    return ( <
        Box p = { 3 }
        display = "flex"
        justifyContent = "center"
        minHeight = "100vh"
        alignItems = "center" >
        <
        StyledCard >
        <
        CardContent sx = {
            { p: 4 } } >
        <
        Box textAlign = "center"
        mb = { 3 } > { getStatusIcon() } <
        Typography variant = "h4"
        sx = {
            { mt: 2, mb: 1 } } > { getStatusMessage() } <
        /Typography> <
        Typography variant = "body1"
        color = "text.secondary" > { getStatusDescription() } <
        /Typography> <
        /Box>

        {
            error && ( <
                Alert severity = "error"
                sx = {
                    { mb: 3 } } > { error } <
                /Alert>
            )
        }

        {
            success && ( <
                Alert severity = "success"
                sx = {
                    { mb: 3 } } >
                Password reset successfully!Redirecting to login... <
                /Alert>
            )
        }

        {
            verificationStatus === 'verified' && !success && ( <
                Box >
                <
                Divider sx = {
                    { my: 3 } }
                />

                <
                Typography variant = "h6"
                gutterBottom >
                Set New Password <
                /Typography>

                <
                TextField fullWidth label = "New Password"
                type = { showPassword ? 'text' : 'password' }
                value = { formData.newPassword }
                onChange = { handleInputChange('newPassword') }
                margin = "normal"
                InputProps = {
                    {
                        startAdornment: ( <
                            InputAdornment position = "start" >
                            <
                            LockIcon color = "action" / >
                            <
                            /InputAdornment>
                        ),
                        endAdornment: ( <
                            InputAdornment position = "end" >
                            <
                            IconButton onClick = {
                                () => setShowPassword(!showPassword) }
                            edge = "end" >
                            { showPassword ? < VisibilityOffIcon / > : < VisibilityIcon / > } <
                            /IconButton> <
                            /InputAdornment>
                        )
                    }
                }
                />

                {
                    formData.newPassword && ( <
                        Box mb = { 2 } >
                        <
                        Box display = "flex"
                        alignItems = "center"
                        gap = { 1 }
                        mb = { 1 } >
                        <
                        Typography variant = "body2"
                        color = "text.secondary" >
                        Password Strength:
                        <
                        /Typography> <
                        Chip label = { getPasswordStrengthText() }
                        color = { getPasswordStrengthColor() }
                        size = "small" /
                        >
                        <
                        /Box> <
                        LinearProgress variant = "determinate"
                        value = {
                            (passwordStrength / 5) * 100 }
                        color = { getPasswordStrengthColor() }
                        sx = {
                            { height: 4, borderRadius: 2 } }
                        /> <
                        /Box>
                    )
                }

                {
                    passwordErrors.length > 0 && ( <
                        Alert severity = "error"
                        sx = {
                            { mb: 2 } } >
                        <
                        Typography variant = "body2" >
                        Password requirements:
                        <
                        /Typography> <
                        ul style = {
                            { margin: 0, paddingLeft: 20 } } > {
                            passwordErrors.map((error, index) => ( <
                                li key = { index } > { error } < /li>
                            ))
                        } <
                        /ul> <
                        /Alert>
                    )
                }

                <
                TextField fullWidth label = "Confirm Password"
                type = { showConfirmPassword ? 'text' : 'password' }
                value = { formData.confirmPassword }
                onChange = { handleInputChange('confirmPassword') }
                margin = "normal"
                InputProps = {
                    {
                        startAdornment: ( <
                            InputAdornment position = "start" >
                            <
                            LockIcon color = "action" / >
                            <
                            /InputAdornment>
                        ),
                        endAdornment: ( <
                            InputAdornment position = "end" >
                            <
                            IconButton onClick = {
                                () => setShowConfirmPassword(!showConfirmPassword) }
                            edge = "end" >
                            { showConfirmPassword ? < VisibilityOffIcon / > : < VisibilityIcon / > } <
                            /IconButton> <
                            /InputAdornment>
                        )
                    }
                }
                />

                <
                Box display = "flex"
                gap = { 2 }
                mt = { 3 } >
                <
                StyledButton variant = "contained"
                onClick = { handlePasswordReset }
                disabled = { resetting || passwordErrors.length > 0 || !formData.newPassword || !formData.confirmPassword }
                fullWidth startIcon = { resetting ? < CircularProgress size = { 20 } /> : <SecurityIcon / > } >
                { resetting ? 'Resetting...' : 'Reset Password' } <
                /StyledButton> <
                /Box> <
                /Box>
            )
        }

        {
            verificationStatus === 'failed' && ( <
                Box >
                <
                Divider sx = {
                    { my: 3 } }
                />

                <
                Typography variant = "h6"
                gutterBottom >
                Token Invalid or Expired <
                /Typography>

                <
                Typography variant = "body2"
                color = "text.secondary"
                paragraph >
                The password reset token is invalid or has expired.Please request a new password reset. <
                /Typography>

                <
                Box display = "flex"
                gap = { 2 }
                mt = { 3 } >
                <
                StyledButton variant = "contained"
                onClick = {
                    () => navigate('/forgot-password') }
                fullWidth >
                Request New Reset <
                /StyledButton> <
                StyledButton variant = "outlined"
                onClick = {
                    () => navigate('/login') }
                fullWidth >
                Back to Login <
                /StyledButton> <
                /Box> <
                /Box>
            )
        }

        <
        Divider sx = {
            { my: 3 } }
        />

        <
        Box >
        <
        Typography variant = "h6"
        gutterBottom >
        Need help ?
        <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary" >
        If you 're having trouble resetting your password, please contact our support team. <
        /Typography> <
        Box display = "flex"
        gap = { 1 }
        mt = { 2 } >
        <
        Button variant = "outlined"
        size = "small" >
        Contact Support <
        /Button> <
        Button variant = "outlined"
        size = "small" >
        Help Center <
        /Button> <
        /Box> <
        /Box> <
        /CardContent> <
        /StyledCard> <
        /Box>
    );
};

export default PasswordReset;